/**
 * Airport infrastructure models (Phase 5): base tiers, line and heavy
 * maintenance, owned ground handling, in-house catering, lounges and MRO
 * leasing.
 *
 * Shared identifiers and money types come from `../models`; nothing here
 * redefines a baseline schema. `AirlineState` is the snapshot the hub engine
 * reads. It is built from the live engine state rather than stored.
 */

import type { AirportId, ClassMap, SlotHolding, UsdAmount } from '../models';

/** Base classification, ordered from smallest to largest. */
export type BaseTier = 'SPOKE_STATION' | 'FOCUS_CITY' | 'SECONDARY_HUB' | 'PRIMARY_GLOBAL_HUB';

export const BASE_TIERS: readonly BaseTier[] = ['SPOKE_STATION', 'FOCUS_CITY', 'SECONDARY_HUB', 'PRIMARY_GLOBAL_HUB'];

export type LoungeTier = 'THIRD_PARTY_CONTRACT' | 'DEDICATED_BUSINESS_LOUNGE' | 'FLAGSHIP_FIRST_LOUNGE';

/** Lounge tiers ordered from the cheapest product to the most expensive. */
export const LOUNGE_TIERS: readonly LoungeTier[] = [
  'THIRD_PARTY_CONTRACT',
  'DEDICATED_BUSINESS_LOUNGE',
  'FLAGSHIP_FIRST_LOUNGE',
];

/**
 * One investment at one airport. A project is applied with
 * UPGRADE_AIRPORT_INFRASTRUCTURE and is priced by `quoteInfrastructureUpgrade`.
 */
export type InfrastructureProject =
  | { readonly kind: 'BASE_TIER'; readonly tier: BaseTier }
  | { readonly kind: 'LINE_MAINTENANCE' }
  | { readonly kind: 'HEAVY_HANGAR' }
  | { readonly kind: 'MRO_LEASING'; readonly leasedBays: number }
  | { readonly kind: 'OWNED_GROUND_HANDLING' }
  | { readonly kind: 'IN_HOUSE_CATERING' }
  | { readonly kind: 'LOUNGE'; readonly tier: LoungeTier };

export type InfrastructureProjectKind = InfrastructureProject['kind'];

/** Everything the player has built at one airport. */
export interface AirportInfrastructure {
  readonly airportId: AirportId;
  readonly baseTier: BaseTier;
  readonly lineMaintenance: boolean;
  readonly heavyHangar: boolean;
  /** Hangar bays leased to AI competitor carriers. */
  readonly leasedMroBays: number;
  readonly ownedGroundHandling: boolean;
  readonly inHouseCatering: boolean;
  /** Null means the airport has no lounge product yet. */
  readonly loungeTier: LoungeTier | null;
}

/**
 * The airline facts the hub engine needs. Built by the engine from its own
 * state (cash, legs, maintenance-base data and infrastructure) on each call.
 */
export interface AirlineState {
  readonly cashUSD: UsdAmount;
  /** Scheduled departures per week from each airport. */
  readonly weeklyDeparturesByAirport: Readonly<Record<AirportId, number>>;
  /** Slot licences held. Base tiers above FOCUS_CITY need peak-hour slots here. */
  readonly slotHoldings: readonly SlotHolding[];
  /** Airports with a heavy maintenance base (CompanyHQ.maintenanceBaseIds). */
  readonly maintenanceBaseIds: readonly AirportId[];
  /** Built infrastructure. An absent airport means the default: spoke, nothing built. */
  readonly infrastructure: Readonly<Record<AirportId, AirportInfrastructure>>;
  /** Number of other carriers operating at each airport. */
  readonly competingCarriersByAirport: Readonly<Record<AirportId, number>>;
}

/** Result of pricing one InfrastructureProject against an AirlineState. */
export type InfrastructureQuote =
  | {
      readonly ok: true;
      /** One-off cash cost, USD. */
      readonly capexUSD: UsdAmount;
      /** Infrastructure at the airport once the project is built. */
      readonly next: AirportInfrastructure;
    }
  | { readonly ok: false; readonly reasons: readonly string[] };

/**
 * Effects of an airport's infrastructure on operations, revenue and cost.
 * Multipliers are 1 and additive bonuses are 0 when nothing is built.
 */
export interface HubServiceModifiers {
  readonly airportId: AirportId;
  readonly baseTier: BaseTier;
  /** Share of turnaround delays that line maintenance prevents (0–1). */
  readonly turnaroundDelayPreventionPct: number;
  /** Share of minor mechanical faults that line maintenance mitigates (0–1). */
  readonly minorFaultMitigationPct: number;
  /** Multiplier on C- and D-check ground downtime (1 means unchanged). */
  readonly heavyCheckDowntimeMultiplier: number;
  /** Multiplier on the third-party MRO fee of C- and D-checks (0 means eliminated). */
  readonly heavyCheckFeeMultiplier: number;
  /** Change to the minimum turnaround time, in minutes (negative is faster). */
  readonly minTurnaroundMinutesDelta: number;
  /** Multiplier on the per-turnaround ground handling cost. */
  readonly groundHandlingCostMultiplier: number;
  /** Comfort score points added to each cabin's service score. */
  readonly comfortScoreBonusByClass: ClassMap<number>;
  /** Extra fraction of the base fare that passengers in each cabin accept. */
  readonly fareToleranceBonusByClass: ClassMap<number>;
  /** Additive brand loyalty (0–1), earned only at airports with competing carriers. */
  readonly hubLoyaltyBonus: number;
  /** Hangar bays leased to competitors. */
  readonly leasedMroBays: number;
  /** Recurring operating cost of everything built here, USD per day. */
  readonly dailyOperatingCostUSD: UsdAmount;
  /** Recurring MRO lease income from competitors, USD per day. */
  readonly dailyMroLeaseRevenueUSD: UsdAmount;
}

/** Progress towards the next base tier: the requirement and where the airline stands against it. */
export interface BaseTierProgress {
  readonly tier: BaseTier;
  readonly minWeeklyDepartures: number;
  readonly weeklyDepartures: number;
  readonly minPeakSlotsHeld: number;
  readonly peakSlotsHeld: number;
  readonly stepUpCapexUSD: UsdAmount;
}

/** One buildable project at an airport, priced against the airline's position right now. */
export interface HubProjectQuote {
  /** Stable key from hubProjectKey, used for React keys and button lookup. */
  readonly key: string;
  readonly label: string;
  readonly project: InfrastructureProject;
  readonly quote: { readonly ok: true; readonly capexUSD: UsdAmount } | { readonly ok: false; readonly reasons: readonly string[] };
}

/** One card on the Hubs & Infrastructure screen. */
export interface HubAirportView {
  readonly airportId: AirportId;
  readonly baseTier: BaseTier;
  readonly infrastructure: AirportInfrastructure;
  readonly weeklyDepartures: number;
  readonly peakSlotsHeld: number;
  readonly competingCarriers: number;
  /** Null once the airport is already at the top tier. */
  readonly nextTier: BaseTierProgress | null;
  /** Recurring running cost of everything built here, USD per day. */
  readonly dailyOperatingCostUSD: UsdAmount;
  /** Recurring MRO lease income from competitor overhauls, USD per day. */
  readonly dailyMroLeaseRevenueUSD: UsdAmount;
  readonly projects: readonly HubProjectQuote[];
}
